import { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Collapse,
  Chip,
  Stack,
  InputAdornment,
  IconButton,
  Button,
  ButtonGroup,
  TableSortLabel,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import { formatNumber as formatNumberUtil } from "../../../utils/formatNumber";

const TAX_RATE = 0.07;

const STATUS_OPTIONS = [
  { label: "ส่งแล้ว", value: "sent" },
  { label: "ยืนยันแล้ว", value: "confirm" },
  { label: "รับเงินแล้ว", value: "payed" },
];

export default function SalesByProductSummary() {
  const formatDate = (d) => d.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]); // grouped by product
  const [openSet, setOpenSet] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0].value);

  // sort
  const [orderBy, setOrderBy] = useState("productTitle");
  const [order, setOrder] = useState("asc");

  // inventory avg cost
  const [invById, setInvById] = useState({});
  const [invByName, setInvByName] = useState({});

  const cellStyle = { border: "1px solid #ddd" };

  // ====== Inventory: avg cost ======
  useEffect(() => {
    const loadInventory = async () => {
      try {
        const invRes = await services.getInventory();
        const invRaw = invRes?.data?.data ?? invRes?.data ?? invRes ?? [];
        const _byId = {};
        const _byName = {};
        (Array.isArray(invRaw) ? invRaw : [invRaw]).forEach((rec) => {
          const avg =
            Number(
              rec?.avgCostPerUnit ??
                rec?.averageCost ??
                rec?.avgCost ??
                rec?.costPerUnit ??
                rec?.cost ??
                0
            ) || 0;
          const pid =
            rec?.productId ?? rec?.product?.id ?? rec?.id ?? rec?.product_id ?? null;
          const pname =
            rec?.productName ??
            rec?.product?.title ??
            rec?.name ??
            rec?.product_name ??
            null;

          if (pid != null) _byId[String(pid)] = { avgCostPerUnit: avg, name: pname ?? undefined };
          if (pname) _byName[String(pname).trim()] = { avgCostPerUnit: avg, id: pid ?? undefined };
        });
        setInvById(_byId);
        setInvByName(_byName);
      } catch (e) {
        console.error("loadInventory error", e);
        setInvById({});
        setInvByName({});
      }
    };
    loadInventory();
  }, []);

  // ====== Fetch sales ======
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getSaleTransactions({
          ...(selectedStatus ? { status: selectedStatus } : {}),
          startDate,
          endDate,
        });
        const raw = res?.data?.data ?? res?.data ?? res ?? [];
        console.log('raw',raw)
        const grouped = groupByProduct(raw, invById, invByName);
        setRows(grouped);
        setOpenSet(new Set());
      } catch (e) {
        console.error("fetch error", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedStatus, invById, invByName]);

  // ====== Filter ======
  const filteredRows = useMemo(() => {
    const q = (searchQuery || "").toLowerCase().trim();
    if (!q) return rows;
    return rows.filter((r) => {
      const code = String(r.productCode ?? "").toLowerCase();
      const title = String(r.productTitle ?? "").toLowerCase();
      const cat = String(r.categoryMain ?? "").toLowerCase();
      const sub = String(r.categorySub ?? "").toLowerCase();
      return code.includes(q) || title.includes(q) || cat.includes(q) || sub.includes(q);
    });
  }, [rows, searchQuery]);

  // ====== Sorting (main) ======
  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    data.sort((a, b) => {
      let aVal, bVal;
      switch (orderBy) {
        case "productCode":
          aVal = a.productCode ?? ""; bVal = b.productCode ?? ""; break;
        case "productTitle":
          aVal = a.productTitle ?? ""; bVal = b.productTitle ?? ""; break;
        case "categoryMain":
          aVal = a.categoryMain ?? ""; bVal = b.categoryMain ?? ""; break;
        case "categorySub":
          aVal = a.categorySub ?? ""; bVal = b.categorySub ?? ""; break;

        case "sendWeight":
        case "deductWeight":
        case "destWeight":
        case "avgPrice":
        case "avgCost":
        case "amountBeforeVat":
        case "costAmount":
        case "profitBeforeTax":
          aVal = Number(a[orderBy] || 0);
          bVal = Number(b[orderBy] || 0);
          break;

        default:
          aVal = 0; bVal = 0;
      }
      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, "th");
        return order === "asc" ? cmp : -cmp;
      }
      const cmp = (aVal || 0) - (bVal || 0);
      return order === "asc" ? cmp : -cmp;
    });
    return data;
  }, [filteredRows, orderBy, order]);

  const requestSort = (key) => {
    if (["unit"].includes(key)) return; // หน่วยไม่ให้ sort
    if (orderBy === key) setOrder((p) => (p === "asc" ? "desc" : "asc"));
    else {
      setOrderBy(key);
      setOrder(["productCode", "productTitle", "categoryMain", "categorySub"].includes(key) ? "asc" : "desc");
    }
  };

  // ====== Summary footer ======
  const summary = useMemo(() => {
    let send = 0, deduct = 0, dest = 0, before = 0, cost = 0, profit = 0;
    sortedRows.forEach((r) => {
      send += r.sendWeight || 0;
      deduct += r.deductWeight || 0;
      dest += r.destWeight || 0;
      before += r.amountBeforeVat || 0;
      cost += r.costAmount || 0;
      profit += r.profitBeforeTax || 0;
    });
    return { send, deduct, dest, before, cost, profit };
  }, [sortedRows]);

  // ====== Export helpers ======
  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportSummary = () => {
    const dataset = sortedRows.map((r) => ({
      รหัส: r.productCode ?? "",
      สินค้า: r.productTitle ?? "",
      หมวดหมู่หลัก: r.categoryMain ?? "",
      หมวดหมู่ย่อย: r.categorySub ?? "",
      น้ำหนักส่งสุทธิ: fix2(r.sendWeight),
      น้ำหนักหัก: fix2(r.deductWeight),
      น้ำหนักปลายทางสุทธิ: fix2(r.destWeight),
      หน่วย: r.unit || "กก.",
      ราคาขาย: fix2(r.avgPrice),
      ราคาต้นทุน: fix2(r.avgCost),
      ยอดขายก่อนภาษี: fix2(r.amountBeforeVat),
      ต้นทุน: fix2(r.costAmount),
      กำไรก่อนภาษี: fix2(r.profitBeforeTax),
    }));
    exportToCSV(dataset, `sales_by_product_${startDate}_to_${endDate}.csv`);
  };

  const handleExportAllItems = () => {
    const rowsFlat = [];
    sortedRows.forEach((r) => {
      (r.children || []).forEach((c) => {
        rowsFlat.push({
          รหัส: r.productCode ?? "",
          สินค้า: r.productTitle ?? "",
          ซัพพลายเออร์: c.customerName ?? "-",
          น้ำหนักส่งสุทธิ: fix2(c.sendWeight),
          น้ำหนักหัก: fix2(c.deductWeight),
          น้ำหนักปลายทางสุทธิ: fix2(c.destWeight),
          หน่วย: c.unit || "กก.",
          ราคาขาย: fix2(c.pricePerUnit),
          ราคาต้นทุน: fix2(c.costPerUnit),
          ยอดก่อนภาษี: fix2(c.amountBeforeVat),
          ต้นทุน: fix2(c.costAmount),
          กำไรก่อนภาษี: fix2(c.profitBeforeTax),
        });
      });
    });
    exportToCSV(rowsFlat, `sales_by_product_items_${startDate}_to_${endDate}.csv`);
  };

  // ====== UI ======
  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <AppBreadcrumbs />

      {/* ค้นหา/ช่วงเวลา */}
      <Box display="flex" justifyContent="flex-end" gap={2} my={2} flexWrap="wrap">
        <TextField
          label="ค้นหา"
          value={searchQuery}
          placeholder="รหัส / ชื่อสินค้า / หมวดหมู่"
          onChange={(e) => setSearchQuery(e.target.value)}
          size="medium"
          sx={{ minWidth: 380 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="เริ่มต้น"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="สิ้นสุด"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* หัวข้อ + Filter + Export */}
      <Box display="flex" justifyContent="space-between" alignItems="center" my={1} gap={2} flexWrap="wrap">
        <Typography variant="h6">สรุปยอดขายสินค้ารายชนิด</Typography>

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <ButtonGroup variant="outlined">
            {STATUS_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="large"
                onClick={() => setSelectedStatus(opt.value)}
                variant={selectedStatus === opt.value ? "contained" : "outlined"}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>

          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ContentCopyIcon />}
              onClick={handleExportSummary}
            >
              ข้อมูลหลัก
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ContentCopyIcon />}
              onClick={handleExportAllItems}
            >
              ข้อมูลย่อยทั้งหมด
            </Button>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
              <TableRow>
                <TableCell sx={{ ...cellStyle, width: 48 }} />

                {/* รหัส */}
                <TableCell sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "productCode" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "productCode"}
                    direction={orderBy === "productCode" ? order : "asc"}
                    onClick={() => requestSort("productCode")}
                  >
                    รหัส
                  </TableSortLabel>
                </TableCell>

                {/* สินค้า */}
                <TableCell sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "productTitle" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "productTitle"}
                    direction={orderBy === "productTitle" ? order : "asc"}
                    onClick={() => requestSort("productTitle")}
                  >
                    สินค้า
                  </TableSortLabel>
                </TableCell>

                {/* หมวดหมู่หลัก */}
                <TableCell sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "categoryMain" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "categoryMain"}
                    direction={orderBy === "categoryMain" ? order : "asc"}
                    onClick={() => requestSort("categoryMain")}
                  >
                    หมวดหมู่หลัก
                  </TableSortLabel>
                </TableCell>

                {/* หมวดหมู่ย่อย */}
                <TableCell sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "categorySub" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "categorySub"}
                    direction={orderBy === "categorySub" ? order : "asc"}
                    onClick={() => requestSort("categorySub")}
                  >
                    หมวดหมู่ย่อย
                  </TableSortLabel>
                </TableCell>

                {/* น้ำหนักส่งสุทธิ */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "sendWeight" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "sendWeight"}
                    direction={orderBy === "sendWeight" ? order : "desc"}
                    onClick={() => requestSort("sendWeight")}
                  >
                    น้ำหนักส่งสุทธิ
                  </TableSortLabel>
                </TableCell>

                {/* น้ำหนักหัก */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "deductWeight" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "deductWeight"}
                    direction={orderBy === "deductWeight" ? order : "desc"}
                    onClick={() => requestSort("deductWeight")}
                  >
                    น้ำหนักหัก
                  </TableSortLabel>
                </TableCell>

                {/* น้ำหนักปลายทางสุทธิ */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "destWeight" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "destWeight"}
                    direction={orderBy === "destWeight" ? order : "desc"}
                    onClick={() => requestSort("destWeight")}
                  >
                    น้ำหนักปลายทางสุทธิ
                  </TableSortLabel>
                </TableCell>

                {/* หน่วย (ไม่ sortable) */}
                <TableCell sx={cellStyle}>หน่วย</TableCell>

                {/* ราคาขาย */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "avgPrice" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "avgPrice"}
                    direction={orderBy === "avgPrice" ? order : "desc"}
                    onClick={() => requestSort("avgPrice")}
                  >
                    ราคาขาย
                  </TableSortLabel>
                </TableCell>

                {/* ราคาต้นทุน */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "avgCost" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "avgCost"}
                    direction={orderBy === "avgCost" ? order : "desc"}
                    onClick={() => requestSort("avgCost")}
                  >
                    ราคาต้นทุน
                  </TableSortLabel>
                </TableCell>

                {/* ยอดขายก่อนภาษี */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "amountBeforeVat" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "amountBeforeVat"}
                    direction={orderBy === "amountBeforeVat" ? order : "desc"}
                    onClick={() => requestSort("amountBeforeVat")}
                  >
                    ยอดขายก่อนภาษี
                  </TableSortLabel>
                </TableCell>

                {/* ต้นทุน */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "costAmount" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "costAmount"}
                    direction={orderBy === "costAmount" ? order : "desc"}
                    onClick={() => requestSort("costAmount")}
                  >
                    ต้นทุน
                  </TableSortLabel>
                </TableCell>

                {/* กำไรก่อนภาษี */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "profitBeforeTax" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "profitBeforeTax"}
                    direction={orderBy === "profitBeforeTax" ? order : "desc"}
                    onClick={() => requestSort("profitBeforeTax")}
                  >
                    กำไรก่อนภาษี
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedRows.map((r, idx) => {
                const key = String(r.productId ?? r.productCode ?? r.productTitle ?? idx);
                const isOpen = openSet.has(key);
                return (
                  <>
                    <TableRow hover key={key}>
                      <TableCell sx={{ ...cellStyle, width: 48 }}>
                        <IconButton size="small" onClick={() => {
                          setOpenSet((prev) => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key); else next.add(key);
                            return next;
                          });
                        }}>
                          {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={cellStyle}>{r.productCode ?? "-"}</TableCell>
                      <TableCell sx={cellStyle}>{r.productTitle ?? "-"}</TableCell>
                      <TableCell sx={cellStyle}>{r.categoryMain ?? "-"}</TableCell>
                      <TableCell sx={cellStyle}>{r.categorySub ?? "-"}</TableCell>

                      <TableCell align="right" sx={cellStyle}>{fmt(r.sendWeight)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(r.deductWeight)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(r.destWeight)}</TableCell>
                      <TableCell sx={cellStyle}>{r.unit || "กก."}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(r.avgPrice)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(r.avgCost)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(r.amountBeforeVat)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(r.costAmount)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...cellStyle,
                          color: (r.profitBeforeTax || 0) > 0 ? "green" : (r.profitBeforeTax || 0) < 0 ? "red" : "inherit",
                          fontWeight: "bold",
                        }}
                      >
                        {fmt(r.profitBeforeTax)}
                      </TableCell>
                    </TableRow>

                    {/* Sub: breakdown by customer */}
                    <TableRow>
                      <TableCell style={{ padding: 0 }} colSpan={15}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box p={2}>
                            <Typography variant="subtitle2" mb={1}>รายการซัพพลายเออร์ในสินค้าเดียวกัน</Typography>
                            <Table>
                              <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                                <TableRow>
                                  <TableCell sx={cellStyle}>ซัพพลายเออร์</TableCell>
                                  <TableCell align="right" sx={cellStyle}>น้ำหนักส่งสุทธิ</TableCell>
                                  <TableCell align="right" sx={cellStyle}>น้ำหนักหัก</TableCell>
                                  <TableCell align="right" sx={cellStyle}>น้ำหนักปลายทางสุทธิ</TableCell>
                                  <TableCell sx={cellStyle}>หน่วย</TableCell>
                                  <TableCell align="right" sx={cellStyle}>ราคาขาย</TableCell>
                                  <TableCell align="right" sx={cellStyle}>ราคาต้นทุน</TableCell>
                                  <TableCell align="right" sx={cellStyle}>ยอดก่อนภาษี</TableCell>
                                  <TableCell align="right" sx={cellStyle}>ต้นทุน</TableCell>
                                  <TableCell align="right" sx={cellStyle}>กำไรก่อนภาษี</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(r.children || []).map((c, i) => (
                                  <TableRow key={i}>
                                    <TableCell sx={cellStyle}>{c.customerName ?? "-"}</TableCell>
                                    <TableCell align="right" sx={cellStyle}>{formatNumber(c.sendWeight)}</TableCell>
                                    <TableCell align="right" sx={cellStyle}>{formatNumber(c.deductWeight)}</TableCell>
                                    <TableCell align="right" sx={cellStyle}>{formatNumber(c.destWeight)}</TableCell>
                                    <TableCell sx={cellStyle}>{c.unit || "กก."}</TableCell>
                                    <TableCell align="right" sx={cellStyle}>{formatNumber(c.pricePerUnit)}</TableCell>
                                    <TableCell align="right" sx={cellStyle}>{formatNumber(c.costPerUnit)}</TableCell>
                                    <TableCell align="right" sx={cellStyle}>{formatNumber(c.amountBeforeVat)}</TableCell>
                                    <TableCell align="right" sx={cellStyle}>{formatNumber(c.costAmount)}</TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        ...cellStyle,
                                        color: (c.profitBeforeTax || 0) > 0 ? "green" : (c.profitBeforeTax || 0) < 0 ? "red" : "inherit",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {formatNumber(c.profitBeforeTax)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                );
              })}
            </TableBody>

            <TableFooter>
              <TableRow sx={{ backgroundColor: "#fafafa", "& td": { fontSize: "14px", fontWeight: "bold" } }}>
                <TableCell sx={cellStyle} />
                <TableCell sx={cellStyle} colSpan={4}>รวม {sortedRows.length.toLocaleString("th-TH")} ชนิด</TableCell>
                <TableCell align="right" sx={cellStyle}>{fmt(summary.send)}</TableCell>
                <TableCell align="right" sx={cellStyle}>{fmt(summary.deduct)}</TableCell>
                <TableCell align="right" sx={cellStyle}>{fmt(summary.dest)}</TableCell>
                <TableCell sx={cellStyle}>กก.</TableCell>
                <TableCell sx={cellStyle}></TableCell>
                <TableCell sx={cellStyle}></TableCell>
                <TableCell align="right" sx={cellStyle}>{fmt(summary.before)}</TableCell>
                <TableCell align="right" sx={cellStyle}>{fmt(summary.cost)}</TableCell>
                <TableCell align="right" sx={{ ...cellStyle, color: summary.profit > 0 ? "green" : summary.profit < 0 ? "red" : "inherit" }}>
                  {fmt(summary.profit)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/* ================= Helpers ================= */

function groupByProduct(data = [], invById = {}, invByName = {}) {
  const prodMap = {};

  (Array.isArray(data) ? data : [data]).forEach((row) => {
    const isVat = row?.isVat === true;

    (row.items || []).forEach((it) => {
      const pid = it.productId ?? it.product?.id ?? null;
      const pcode = it.product?.code ?? it.code ?? pid ?? "";
      const ptitle = String(it.productName ?? it.product?.title ?? "-").trim();

      const unit = it.unit || "กก.";
      const destW = num(it.netWeight ?? it.weight);
      const sendW = num(it.sendWeight ?? destW);

      const pricePerUnit = num(it.pricePerUnit);
      const amountBefore = num(it.amountBeforeVat ?? destW * pricePerUnit);
      const vat = isVat ? num(it.vat ?? amountBefore * TAX_RATE) : 0;
      const netPrice = amountBefore + vat;

      // ---- cost from inventory ----
      const invCostById = pid != null ? invById[String(pid)]?.avgCostPerUnit : undefined;
      const invCostByName = ptitle ? invByName[ptitle]?.avgCostPerUnit : undefined;
      const costPerUnit = num(it.costPerUnit ?? invCostById ?? invCostByName ?? 0);
      const costAmount = num(it.costAmount ?? costPerUnit * destW);

      const key = String(pid ?? ptitle);
      if (!prodMap[key]) {
        prodMap[key] = {
          productId: pid,
          productCode: pcode,
          productTitle: ptitle,
          categoryMain: it.product?.category?.name ?? it.category ?? "-",
          categorySub: it.product?.subCategory?.name ?? it.subCategory ?? "-",
          unit,
          sendWeight: 0,
          destWeight: 0,
          deductWeight: 0,
          amountBeforeVat: 0,
          costAmount: 0,
          profitBeforeTax: 0,
          _childMap: new Map(),
        };
      }

      const rec = prodMap[key];
      rec.sendWeight += sendW;
      rec.destWeight += destW;
      rec.deductWeight += (sendW - destW);
      rec.amountBeforeVat += amountBefore;
      rec.costAmount += costAmount;
      rec.profitBeforeTax += (amountBefore - costAmount);

      // ===== รวมซัพพลายเออร์ที่ซ้ำกันใน sub =====
      const customerId = row.customerId ?? row.customer?.id ?? null;
      const customerName = row.customer?.name ?? row.customerName ?? "-";
      const custKey = String(customerId ?? customerName);

      if (!rec._childMap.has(custKey)) {
        rec._childMap.set(custKey, {
          customerId,
          customerName,
          unit,    
          sendWeight: 0,
          destWeight: 0,
          deductWeight: 0,
          _amountBeforeVat: 0,
          _costAmount: 0,
        });
      }

      const child = rec._childMap.get(custKey);
      child.sendWeight += sendW;
      child.destWeight += destW;
      child.deductWeight += (sendW - destW);
      child._amountBeforeVat += amountBefore;
      child._costAmount += costAmount;
    });
  });

  // แปลง children และคำนวณราคาเฉลี่ยถ่วงน้ำหนัก
  return Object.values(prodMap).map((r) => {
    const children = Array.from(r._childMap.values()).map((c) => {
      const d = c.destWeight || 0;
      const pricePerUnit = d > 0 ? c._amountBeforeVat / d : 0;
      const costPerUnit  = d > 0 ? c._costAmount / d : 0;
      const amountBeforeVat = c._amountBeforeVat;
      const costAmount = c._costAmount;
      const profitBeforeTax = amountBeforeVat - costAmount;

      return {
        customerId: c.customerId,
        customerName: c.customerName,
        unit: c.unit,
        sendWeight: c.sendWeight,
        destWeight: c.destWeight,
        deductWeight: c.deductWeight,
        pricePerUnit,
        costPerUnit,
        amountBeforeVat,
        costAmount,
        profitBeforeTax,
      };
    });

    const denom = r.destWeight || 0;
    return {
      ...r,
      avgPrice: denom > 0 ? r.amountBeforeVat / denom : 0,
      avgCost: denom > 0 ? r.costAmount / denom : 0,
      children,       // ✅ ใช้ children ที่รวมแล้ว
      _childMap: undefined,
    };
  });
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function fmt(v) {
  return num(v).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatNumber(v) {
  return typeof formatNumberUtil === "function" ? formatNumberUtil(v) : fmt(v);
}
function fix2(v) {
  return Number(num(v)).toFixed(2);
}
